/**
 * Typed game event bus. DAISE, achievements, audio, and scoring all hang off
 * this — game systems emit facts, presentation layers react.
 */
export interface EventPayloads {
  player_spawn: { levelId: string }
  player_starving: { hp: number }
  player_death: { cause: string; floor: number }
  player_death_cheap: { cause: string; floor: number }
  player_hurt: { hp: number; by: string }
  high_score: { score: number }
  floor_transition: { floor: number; act: number; levelId: string }
  boss_intro: { bossId: string; name: string }
  boss_phase: { bossId: string; phase: number }
  boss_kill: { bossId: string; name: string }
  achievement: { id: string; title: string }
  magic_used: { remaining: number }
  death_type_spawn: { x: number; y: number }
  death_type_removed: Record<string, never>
  generator_destroyed: { genType: string; remaining: number }
  enemy_killed: { type: string; by: 'shot' | 'magic' | 'trap' }
  enemy_hit: Record<string, never>
  enemy_shot: Record<string, never>
  pickup: { type: string }
  key_pickup: Record<string, never>
  key_used: Record<string, never>
  door_opened: Record<string, never>
  door_unlocked: Record<string, never>
  trap_hit: Record<string, never>
  /** a spent life (respawn incoming) — distinct from the run-ending player_death */
  life_lost: { remaining: number }
  /** every 5th floor reached in endless mode */
  endless_milestone: { floor: number }
  secret_found: Record<string, never>
  teleport: Record<string, never>
  /** a circuit relay latched; remaining counts down to shutters_open */
  relay_latched: { remaining: number }
  shutters_open: Record<string, never>
  treasure: { value: number }
  level_clear: { floor: number; timeMs: number }
  campaign_won: { score: number }
  endless_unlocked: Record<string, never>
  run_start: { mode: 'campaign' | 'endless'; seed: number }
  run_end: { score: number; floor: number; won: boolean }
  score_changed: { score: number; delta: number }
  /** a between-floor requisition was approved (score already deducted) */
  requisition_purchase: { id: string; rank: number; cost: number }
  shot_fired: Record<string, never>
  player_hit_wall_shot: Record<string, never>
  /** a sanctioned armament was collected (temporary weapon) */
  armament_pickup: { id: string }
  /** the held armament ran out of charge; back to the standard-issue blaster */
  armament_exhausted: { id: string }
  /** first-ever OVERRIDE activation (slow-motion) — DAISE notices, once */
  override_engaged: Record<string, never>
  /** a run-scoped amenity was approved on the between-floor panel */
  amenity_purchase: { id: string }
  /** residuals invested: a new amenity permanently joins the stock */
  residual_unlock: { name: string }
}

export type EventName = keyof EventPayloads
export type Handler<E extends EventName> = (payload: EventPayloads[E]) => void

export class EventBus {
  private handlers = new Map<EventName, Set<Handler<EventName>>>()

  on<E extends EventName>(event: E, fn: Handler<E>): () => void {
    let set = this.handlers.get(event)
    if (!set) {
      set = new Set()
      this.handlers.set(event, set)
    }
    set.add(fn as Handler<EventName>)
    return () => set.delete(fn as Handler<EventName>)
  }

  emit<E extends EventName>(event: E, payload: EventPayloads[E]): void {
    const set = this.handlers.get(event)
    if (!set) return
    for (const fn of set) (fn as Handler<E>)(payload)
  }

  clear(): void {
    this.handlers.clear()
  }
}
